export interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface Point {
    x: number;
    y: number;
}

export class Quadtree<T extends Point> {
    private boundary: Rectangle;
    private capacity: number;
    private points: T[];
    private divided: boolean;
    private northeast?: Quadtree<T>;
    private northwest?: Quadtree<T>;
    private southeast?: Quadtree<T>;
    private southwest?: Quadtree<T>;

    constructor(boundary: Rectangle, capacity: number) {
        this.boundary = boundary;
        this.capacity = capacity;
        this.points = [];
        this.divided = false;
    }

    insert(point: T): boolean {
        if (!this.contains(point)) return false;

        if (this.points.length < this.capacity) {
            this.points.push(point);
            return true;
        }

        if (!this.divided) this.subdivide();

        return (
            this.northeast!.insert(point) ||
            this.northwest!.insert(point) ||
            this.southeast!.insert(point) ||
            this.southwest!.insert(point)
        );
    }

    private contains(point: T): boolean {
        return (
            point.x >= this.boundary.x &&
            point.x < this.boundary.x + this.boundary.width &&
            point.y >= this.boundary.y &&
            point.y < this.boundary.y + this.boundary.height
        );
    }

    private subdivide(): void {
        const { x, y, width, height } = this.boundary;
        const w = width / 2;
        const h = height / 2;

        this.northeast = new Quadtree({ x: x + w, y: y, width: w, height: h }, this.capacity);
        this.northwest = new Quadtree({ x: x, y: y, width: w, height: h }, this.capacity);
        this.southeast = new Quadtree({ x: x + w, y: y + h, width: w, height: h }, this.capacity);
        this.southwest = new Quadtree({ x: x, y: y + h, width: w, height: h }, this.capacity);

        this.divided = true;
    }

    query(range: Rectangle, found: T[] = []): T[] {
        if (!this.intersects(range)) return found;

        for (const point of this.points) {
            if (
                point.x >= range.x &&
                point.x < range.x + range.width &&
                point.y >= range.y &&
                point.y < range.y + range.height
            ) {
                found.push(point);
            }
        }

        if (this.divided) {
            this.northwest!.query(range, found);
            this.northeast!.query(range, found);
            this.southwest!.query(range, found);
            this.southeast!.query(range, found);
        }

        return found;
    }

    private intersects(range: Rectangle): boolean {
        return !(
            range.x > this.boundary.x + this.boundary.width ||
            range.x + range.width < this.boundary.x ||
            range.y > this.boundary.y + this.boundary.height ||
            range.y + range.height < this.boundary.y
        );
    }
}
